import React, { Fragment, useEffect } from 'react';
import { useFormik } from 'formik';
import { Checkbox } from 'antd';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Constants, Validators } from 'core';
import { Profil, ProfilParams, RequestError, Section, SectionName } from 'types';
import { path } from 'ramda';

import FormError from 'components/molecules/form-error';
import Input from 'components/organisms/input';
import Button from 'components/molecules/button';
import history from 'app-router/history';
import { ClearStateSliceActions } from 'common/config/store';
import { useIntl } from 'common/config/i18n';

const WrapperForm = styled.header`
  margin-bottom: 20px;
`;
const WrapperInput = styled.div`
  margin-top: 20px;
`;
const radioStyle = {
  height: '30px',
  lineHeight: '30px',
  marginLeft: 0,
};
const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;
const ButtonWrapper = styled.div`
  display: flex;
  width: 100%;
`;

interface Props {
  id: string;
  onUpdate: (values: ProfilParams) => void;
  sections?: Section[];
  profil?: Profil;
  status?: 'loading' | 'error' | 'success';
  error?: RequestError;
}

type Permission = {
  read: boolean;
  write: boolean;
};

type FormikValuesWithoutName = { [key in SectionName]?: Permission };
type FormikValues = FormikValuesWithoutName & {
  name: string;
};

const Form = ({ id, onUpdate, sections, profil, status, error }: Props) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const InitialsValues: FormikValuesWithoutName | undefined = sections?.reduce(
    (obj, item) => Object.assign(obj, { [item.name]: undefined }),
    {}
  );

  const initialValues: FormikValues = { name: '', ...InitialsValues };

  const formik = useFormik({
    initialValues,
    validate: (values) => Validators.modifyProfil()(values),
    onSubmit: (values) => {
      const mappedValues = Object.entries(values).map((e) => ({ [e[0]]: e[1] }));
      const updateProfileParams: ProfilParams = {
        id: parseInt(id),
        name: values.name,
        sectionsAndPermissions: mappedValues?.slice(1)?.map((x, index) => {
          return {
            id: index + 1,
            ...((sections ? x[sections[index].name] : {}) as Omit<Permission, 'id'>),
          };
        }),
      };
      onUpdate(updateProfileParams);
    },
  });

  useEffect(() => {
    formik.setFieldValue('name', profil?.name);
    profil?.sections.map((x) => {
      formik.setFieldValue(x?.name, {
        read: profil?.sections?.find((y) => y.name === x.name)?.read,
        write: profil?.sections?.find((y) => y.name === x.name)?.write,
      });
      return {
        read: profil?.sections?.find((y) => y.name === x.name)?.read,
        write: profil?.sections?.find((y) => y.name === x.name)?.write,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User' }));
  }, [dispatch]);

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <WrapperForm>
          <Input
            width="270px"
            upLabel={t('form', 'NAME')}
            name="name"
            onChange={formik.handleChange}
            value={formik.values.name}
          />
          <FormError formik={formik} fieldName="name" />
          {sections?.map((section) => {
            const calculateValue = (s: Section) => {
              if (formik?.values[s?.name]?.read && formik?.values[s?.name]?.write) return ['write', 'read'];
              if (formik?.values[s?.name]?.read) return ['read'];
              if (formik?.values[s.name]?.write) return ['write'];
            };

            return (
              <Fragment key={section.id}>
                <WrapperInput>
                  <UpLabel>{t('form', section.name)}</UpLabel>
                  <Checkbox.Group
                    value={calculateValue(section)}
                    onChange={(checkedValues) =>
                      formik.setFieldValue(section.name, {
                        write: checkedValues.includes('write'),
                        read: checkedValues.includes('read'),
                      })
                    }
                  >
                    {section?.readLabel && (
                      <Checkbox style={radioStyle} value="read">
                        {section?.readLabel}
                      </Checkbox>
                    )}
                    {section?.writeLabel && (
                      <Checkbox style={radioStyle} value="write">
                        {section?.writeLabel}
                      </Checkbox>
                    )}
                  </Checkbox.Group>
                </WrapperInput>
                <FormError formik={formik} fieldName={section.name} />
              </Fragment>
            );
          })}
        </WrapperForm>
        {error && (
          <FormError
            message={
              error === Constants.ALREADY_EXISTED_PROBLEM_CODE
                ? t('form', 'FORM_PROFIL_ALLREADY_EXIST')
                : t('form', 'FORM_UNKNOWN_ERROR')
            }
          />
        )}
        <ButtonWrapper>
          <Button height="35px" width="100%" borderRadius="20px" loading={status === 'loading'} htmlType="submit">
            {t('Buttons', 'SAVE_BUTTON')}
          </Button>
          <Button
            height="35px"
            margin=" 0 0 0 2px"
            width="100%"
            borderRadius="20px"
            onClick={() => {
              history.push('/profils');
            }}
          >
            {t('Buttons', 'CANCEL_BUTTON')}
          </Button>
        </ButtonWrapper>
      </form>
    </>
  );
};
export default Form;
